import {
  Component,
  computed,
  inject,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Data, NavigationEnd, Router, RouterModule } from '@angular/router';
import { PromptComponent } from '@components/prompt/prompt.component';
import { Dataset, ItemListResponse, Permission, Record } from '@models';
import {
  AuthService,
  DatasetService,
  NotificationService,
  TitleService,
} from '@services';
import { Clipboard } from '@angular/cdk/clipboard';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { httpResource } from '@angular/common/http';
import { environment } from '@environment';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-dataset-item',
  templateUrl: './item.component.html',
  styleUrls: ['./item.component.scss'],
  imports: [
    RouterModule,
    CommonModule,
    DatePipe,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatMenuModule,
    MatTooltipModule,
  ],
})
export class DatasetItemComponent {
  @ViewChild('publishPromptTemplate')
  publishPromptTemplateRef!: TemplateRef<unknown>;

  route = inject(ActivatedRoute);
  clipboard = inject(Clipboard);
  dialog = inject(MatDialog);
  router = inject(Router);
  notificationService = inject(NotificationService);
  datasetService = inject(DatasetService);
  titleService = inject(TitleService);
  authService = inject(AuthService);

  dataset = signal<Dataset>(this.route.snapshot.data['dataset']);
  permissions = signal<Permission>(this.route.snapshot.data['permissions']);
  currentRoute = signal('');

  records = httpResource<ItemListResponse<Record>>(() =>
    `${environment.kinkoHost}/dataset/${this.dataset().id}/record/?take=1`);
  hasRecords = computed(() => this.records.hasValue() && this.records.value().items.length);

  types = computed(() => this.authService.user() ? 'public,archived,draft' : 'public,archived');
  versionsResource = httpResource<Dataset[]>(
    () => `${environment.kinkoHost}/dataset/?seriesId=${this.dataset().seriesId}&orderBy=created:desc&type=${this.types()}`,
    {
      parse: (r) => (r as ItemListResponse<Dataset>)?.items || []
    });
  versions = computed(() => {
    return this.versionsResource.hasValue() ? this.versionsResource.value() : [];
  });
  latestVersion = computed(() => {
    return this.versions().length ? this.versions()[0] : null;
  });

  mayUpdate = computed(() => {
    return this.permissions()?.mayUpdate || this.authService.user()?.accessLevel === 'admin';
  });

  constructor() {
    this.route.data.subscribe((data: Data) => {
      this.dataset.set(data['dataset']);
      this.titleService.setTitle(this.dataset().content.title);
    });

    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.currentRoute.set(e.url.split('/')[3] || '');
      }
    });
  }

  publish() {
    const dialogRef = this.dialog.open(PromptComponent, {
      minWidth: '360px',
      width: '480px',
      data: {
        title: 'Publish dataset',
        content:
          'Confirm to publish this dataset. This action can not be undone.',
        template: this.publishPromptTemplateRef,
      },
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) {
        return;
      }

      this.datasetService
        .publish(this.dataset().id, this.dataset().content)
        .subscribe({
          next: (dataset: Dataset) => {
            this.dataset.set(dataset);
            this.notificationService.success('Dataset published successfully.');
          },
          error: (err) => {
            console.error(err);
            if (err?.status === 422 && err.error?.details) {
              const message = err.error.details?.errors?.required;
              if (message) {
                this.notificationService.error(
                  `Failed to publish dataset. ${message}.`
                );
              } else {
                this.notificationService.error(
                  'Failed to publish dataset.'
                );
              }
              return;
            } else if (err?.status === 400) {
              this.notificationService.error(
                'Failed to publish dataset. Please add at least one author and one attachment, then try again.',
              );
            } else {
              this.notificationService.error('Failed to publish dataset.');
            }
          },
        });
    });
  }

  delete() {
    const dialogRef = this.dialog.open(PromptComponent, {
      data: {
        title: 'Delete dataset',
        content:
          'Confirm to delete this dataset. This action can not be undone and all data will be lost.',
      },
      minWidth: '360px',
      width: '480px',
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) {
        return;
      }

      this.datasetService.delete(this.dataset().id).subscribe({
        next: () => {
          this.router.navigate(['/dataset']).finally(() => {
            this.notificationService.success('Dataset deleted successfully.');
          });
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to delete dataset.');
        },
      });
    });
  }

  createNewVersion() {
    this.dialog.open(PromptComponent, {
      data: {
        title: 'Create a new version',
        content:
          'This will create a draft copy of the dataset so you can add or remove files. Your current version will stay live until you publish the draft. Once published, the old version will be archived and hidden from the front page.',
      },
      minWidth: '360px',
      width: '480px',
    }).afterClosed().subscribe((r: boolean) => {
      if (!r) {
        return;
      }

      const dataset = this.dataset();
      this.datasetService.create(dataset.content, dataset.seriesId).subscribe({
        next: (d: Dataset) => {
          this.router.navigate(['dataset', d.id, 'edit']);
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error("Failed to create a new version.");
        },
      });
    });
  }
}
